import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  listEntriesApiV1AdminAccessListGet,
  searchVinciApiV1AdminAccessListVinciSearchGet,
} from "@/api/generated/sdk.gen";
import type {
  ApprovalEntryResponseSchema,
  VinciCandidateSchema,
} from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

/** List approval-list entries (admin). Optional status / text filters. */
export const useAccessList = ({
  status,
  q,
  enabled = true,
}: {
  status?: string;
  q?: string;
  enabled?: boolean;
} = {}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.ACCESS_LIST, status ?? null, q ?? null],
    queryFn: async (): Promise<ApprovalEntryResponseSchema[]> => {
      const response = await listEntriesApiV1AdminAccessListGet({
        client: apiClient,
        query: { status: status || null, q: q || null },
      });
      if ("error" in response && response.error) {
        throw new Error("Failed to load the approval list.");
      }
      return response.data ?? [];
    },
    enabled,
    staleTime: 30 * 1000,
  });
};

/** Search the Vinci replica by email/name (admin add form). */
export const useVinciSearch = ({
  q,
  enabled = true,
}: {
  q: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.VINCI_SEARCH, q],
    queryFn: async (): Promise<VinciCandidateSchema[]> => {
      const response = await searchVinciApiV1AdminAccessListVinciSearchGet({
        client: apiClient,
        query: { q },
      });
      if ("error" in response && response.error) {
        throw new Error("Vinci search failed.");
      }
      return response.data ?? [];
    },
    enabled: enabled && q.trim().length >= 2,
    staleTime: 30 * 1000,
  });
};
